(() => {
  const ROOT_SELECTOR = '[data-linked-variants]';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const nodeFromHTML = (html) => {
    if (!html) return null;
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
  };

  const getMainImage = (card) => {
    const currentSlideImage = card.querySelector('.card__media .slider__item[aria-current="true"] img');
    if (currentSlideImage) return currentSlideImage;

    const visibleMainImage = Array.from(card.querySelectorAll('.card__media .card__main-image'))
      .find((image) => !image.hidden);
    if (visibleMainImage) return visibleMainImage;

    return card.querySelector('.card__media img');
  };

  const captureOriginalState = (card) => {
    const titleLink = card.querySelector('.card__title a.js-prod-link');
    const mediaLinks = Array.from(card.querySelectorAll('.card__media a.js-prod-link'));
    const image = getMainImage(card);
    const price = card.querySelector('.price--top, .price--bottom');
    const sizes = card.querySelector('.product-card-sizes');
    const rating = card.querySelector('.rating');
    const ratingContainer = rating ? rating.parentElement : null;

    return {
      titleText: titleLink ? titleLink.textContent : '',
      titleHref: titleLink ? titleLink.getAttribute('href') : '',
      titleAria: titleLink ? titleLink.getAttribute('aria-label') : null,
      mediaLinks: mediaLinks.map((link) => ({
        href: link.getAttribute('href'),
        ariaLabel: link.getAttribute('aria-label')
      })),
      image: image ? {
        src: image.getAttribute('src'),
        srcset: image.getAttribute('srcset'),
        sizes: image.getAttribute('sizes'),
        alt: image.getAttribute('alt')
      } : null,
      priceHTML: price ? price.outerHTML : '',
      sizesHTML: sizes ? sizes.outerHTML : '',
      hadSizes: Boolean(sizes),
      ratingHTML: ratingContainer ? ratingContainer.outerHTML : ''
    };
  };

  const setActiveTrigger = (root, productId) => {
    root.querySelectorAll('[data-linked-current], [data-linked-trigger]').forEach((trigger) => {
      const isActive = trigger.dataset.productId === String(productId);
      trigger.classList.toggle('product-card-linked-variants__item--active', isActive);
      if (isActive) {
        trigger.setAttribute('aria-current', 'true');
      } else {
        trigger.removeAttribute('aria-current');
      }
    });
  };

  const replacePrice = (card, priceNode) => {
    const currentPrice = card.querySelector('.price--top, .price--bottom');
    if (!priceNode || !currentPrice) return;
    currentPrice.replaceWith(priceNode);
  };

  const replaceRating = (card, ratingHTML) => {
    const rating = card.querySelector('.rating');
    const currentContainer = rating ? rating.parentElement : null;
    const nextContainer = nodeFromHTML(ratingHTML);

    if (nextContainer) {
      if (currentContainer) {
        currentContainer.replaceWith(nextContainer);
        return;
      }

      const priceTop = card.querySelector('.price--top');
      if (priceTop) {
        priceTop.insertAdjacentElement('beforebegin', nextContainer);
        return;
      }

      const infoAnchor = card.querySelector('.card__weight')
        || card.querySelector('.card__subtitle')
        || card.querySelector('.card__title');
      if (infoAnchor) infoAnchor.insertAdjacentElement('afterend', nextContainer);
      return;
    }

    if (currentContainer) currentContainer.remove();
  };

  const replaceSizes = (card, sizesNode) => {
    const currentSizes = card.querySelector('.product-card-sizes');

    if (sizesNode) {
      if (currentSizes) {
        currentSizes.replaceWith(sizesNode);
        return;
      }

      const priceTop = card.querySelector('.price--top');
      if (priceTop) {
        priceTop.insertAdjacentElement('afterend', sizesNode);
        return;
      }

      const title = card.querySelector('.card__title');
      if (title) title.insertAdjacentElement('afterend', sizesNode);
      return;
    }

    if (currentSizes) currentSizes.remove();
  };

  const ensureViewSelectedLink = (card, root) => {
    const quickAdd = card.querySelector('.card__quick-add');
    if (!quickAdd) return null;

    let link = quickAdd.querySelector('.product-card-linked-variants__view-selected');
    if (!link) {
      link = document.createElement('a');
      link.className = 'btn btn--primary w-full product-card-linked-variants__view-selected';
      link.textContent = root.dataset.viewLabel || 'View product';
      link.hidden = true;
      quickAdd.appendChild(link);
    }

    return link;
  };

  const setQuickAddPreview = (card, root, url) => {
    const quickAdd = card.querySelector('.card__quick-add');
    if (!quickAdd) return;

    const link = ensureViewSelectedLink(card, root);
    if (!link) return;

    link.href = url;
    link.hidden = false;
    quickAdd.classList.add('product-card-linked-variants__quick-add--preview');
  };

  const restoreQuickAdd = (card) => {
    const quickAdd = card.querySelector('.card__quick-add');
    if (!quickAdd) return;

    quickAdd.classList.remove('product-card-linked-variants__quick-add--preview');
    const link = quickAdd.querySelector('.product-card-linked-variants__view-selected');
    if (link) link.hidden = true;
  };

  const applyLinkedProduct = (card, root, trigger) => {
    const payload = trigger.nextElementSibling;
    if (!payload || !payload.matches('template[data-linked-payload]')) return;

    const titleLink = card.querySelector('.card__title a.js-prod-link');
    const mediaLinks = Array.from(card.querySelectorAll('.card__media a.js-prod-link'));
    const image = getMainImage(card);
    const priceNode = payload.content.querySelector('[data-linked-price] > .price')?.cloneNode(true) || null;
    const sizesNode = payload.content.querySelector('[data-linked-sizes] > .product-card-sizes')?.cloneNode(true) || null;
    const ratingHTML = payload.content.querySelector('[data-linked-rating]')?.innerHTML.trim() || '';

    const url = payload.dataset.productUrl;
    const title = payload.dataset.productTitle;
    const imageSrc = payload.dataset.imageSrc;
    const imageSrcset = payload.dataset.imageSrcset;
    const imageAlt = payload.dataset.imageAlt;

    if (titleLink) {
      titleLink.textContent = title;
      titleLink.setAttribute('href', url);
      titleLink.setAttribute('aria-label', title);
    }

    mediaLinks.forEach((link) => {
      link.setAttribute('href', url);
      link.setAttribute('aria-label', title);
    });

    if (image) {
      if (imageSrcset) image.setAttribute('srcset', imageSrcset);
      else image.removeAttribute('srcset');

      image.setAttribute('src', imageSrc);
      image.setAttribute('alt', imageAlt || title);
    }

    replacePrice(card, priceNode);
    replaceRating(card, ratingHTML);
    replaceSizes(card, sizesNode);
    setActiveTrigger(root, trigger.dataset.productId);
    setQuickAddPreview(card, root, url);
    card.classList.add('is-linked-variant-preview');
    root.dataset.activeProductId = trigger.dataset.productId;
  };

  const restoreOriginalProduct = (card, root, original) => {
    const titleLink = card.querySelector('.card__title a.js-prod-link');
    const mediaLinks = Array.from(card.querySelectorAll('.card__media a.js-prod-link'));
    const image = getMainImage(card);

    if (titleLink) {
      titleLink.textContent = original.titleText;
      titleLink.setAttribute('href', original.titleHref || '#');
      if (original.titleAria !== null) titleLink.setAttribute('aria-label', original.titleAria);
      else titleLink.removeAttribute('aria-label');
    }

    mediaLinks.forEach((link, index) => {
      const saved = original.mediaLinks[index] || original.mediaLinks[0];
      if (!saved) return;
      if (saved.href !== null) link.setAttribute('href', saved.href);
      if (saved.ariaLabel !== null) link.setAttribute('aria-label', saved.ariaLabel);
      else link.removeAttribute('aria-label');
    });

    if (image && original.image) {
      if (original.image.srcset !== null) image.setAttribute('srcset', original.image.srcset);
      else image.removeAttribute('srcset');

      if (original.image.sizes !== null) image.setAttribute('sizes', original.image.sizes);
      else image.removeAttribute('sizes');

      if (original.image.src !== null) image.setAttribute('src', original.image.src);
      if (original.image.alt !== null) image.setAttribute('alt', original.image.alt);
      else image.removeAttribute('alt');
    }

    const originalPriceNode = nodeFromHTML(original.priceHTML);
    if (originalPriceNode) replacePrice(card, originalPriceNode);
    replaceRating(card, original.ratingHTML);

    const currentSizes = card.querySelector('.product-card-sizes');
    if (original.hadSizes) {
      const originalSizesNode = nodeFromHTML(original.sizesHTML);
      if (originalSizesNode) replaceSizes(card, originalSizesNode);
    } else if (currentSizes) {
      currentSizes.remove();
    }

    setActiveTrigger(root, root.dataset.currentProductId);
    restoreQuickAdd(card);
    card.classList.remove('is-linked-variant-preview');
    root.dataset.activeProductId = root.dataset.currentProductId;
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.linkedVariantsInitialized === 'true') return;

    const card = root.closest('product-card, .card--product');
    if (!card) return;

    root.dataset.linkedVariantsInitialized = 'true';
    root.dataset.activeProductId = root.dataset.currentProductId;

    const original = captureOriginalState(card);
    const currentTrigger = root.querySelector('[data-linked-current]');
    const linkedTriggers = Array.from(root.querySelectorAll('[data-linked-trigger]'));

    if (currentTrigger) {
      currentTrigger.addEventListener('pointerenter', () => {
        if (finePointer.matches) restoreOriginalProduct(card, root, original);
      });

      currentTrigger.addEventListener('click', (event) => {
        if (finePointer.matches || event.detail === 0) return;

        const isAlreadyActive = root.dataset.activeProductId === root.dataset.currentProductId;
        if (isAlreadyActive) return;

        event.preventDefault();
        restoreOriginalProduct(card, root, original);
      });
    }

    linkedTriggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => {
        if (finePointer.matches) applyLinkedProduct(card, root, trigger);
      });

      trigger.addEventListener('click', (event) => {
        if (finePointer.matches || event.detail === 0) return;

        const isAlreadyActive = root.dataset.activeProductId === trigger.dataset.productId;
        if (isAlreadyActive) return;

        event.preventDefault();
        applyLinkedProduct(card, root, trigger);
      });
    });

  };

  const initWithin = (scope) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  const init = () => initWithin(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) initWithin(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
